/* eslint-disable no-restricted-syntax -- The admin's own design system (shared/rd), ported from the Restaurant Dashboard design; admin chose app-local components over @a2z/ui-v2 (2026-10-26). See apps/admin/AGENTS.md. */
import {
  forwardRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type SelectHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { Search } from "lucide-react";
import { Badge, Icon } from "./icons";
import { cx } from "./cx";
import { buttonClass, renderIcon, type ButtonSize, type ButtonVariant, type IconRef } from "./lib";

/** Controlled when `value` is given, otherwise keeps its own state. */
function useControlled<T>(value: T | undefined, fallback: T): [T, (next: T) => void] {
  const [own, setOwn] = useState(fallback);
  return value === undefined ? [own, setOwn] : [value, () => {}];
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** primary: blue fill, one per view · secondary: white + border · subtle · ghost · danger */
  variant?: ButtonVariant;
  /** sm 28 · md 32 (default) · lg 36 · xl 42 */
  size?: ButtonSize;
  icon?: IconRef;
  iconEnd?: IconRef;
  /** 28px square; needs `title` or `aria-label`. */
  iconOnly?: boolean;
  /** Ghost in secondary ink (Cancel). */
  muted?: boolean;
  /** Waiting on the server: a spinner replaces the icon and the button is disabled. */
  loading?: boolean;
  /** The words shown while loading ("Confirming…"); default: the label. */
  loadingLabel?: ReactNode;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", icon, iconEnd, iconOnly, muted, loading, loadingLabel, className, children, type, disabled, ...rest },
  ref,
) {
  const label = loading && loadingLabel != null ? loadingLabel : children;
  const iconSize = size === "sm" ? 14 : 16;
  const iconExtra = variant === "primary" ? { stroke: 2 } : undefined;
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      {...rest}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-label={iconOnly ? (rest["aria-label"] ?? rest.title) : rest["aria-label"]}
      className={buttonClass(variant, size, cx(iconOnly && "rd-btn-icon", muted && "rd-btn-muted", loading && "rd-btn-busy", className))}
    >
      {loading ? <span className="rd-spin" aria-hidden="true" /> : icon ? renderIcon(icon, iconSize, iconExtra) : null}
      {iconOnly ? null : label != null ? <span>{label}</span> : null}
      {!loading && iconEnd ? renderIcon(iconEnd, iconSize, iconExtra) : null}
    </button>
  );
});

type InputBase = {
  /** Text or an icon inside the start edge ("EGP"). */
  prefix?: ReactNode;
  /** Text inside the end edge ("%", "min"). */
  suffix?: ReactNode;
  icon?: IconRef;
  /** sm 28 · md 34 (default) · lg 36 */
  size?: "sm" | "md" | "lg";
  invalid?: boolean;
  className?: string;
  style?: CSSProperties;
};

export type InputProps = InputBase & Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix">;
export type TextAreaProps = Omit<InputBase, "icon"> & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "prefix">;

function Affix({ children }: { children: ReactNode }) {
  return children == null ? null : <span className="rd-input-affix">{children}</span>;
}

function inputFrame(p: InputBase & { disabled?: boolean; area?: boolean }) {
  return cx(
    "rd-input",
    p.size && p.size !== "md" && `rd-input-${p.size}`,
    p.invalid && "rd-input-invalid",
    p.disabled && "rd-input-disabled",
    p.area && "rd-input-area",
    p.className,
  );
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { prefix, suffix, icon, size, invalid, className, style, ...rest },
  ref,
) {
  return (
    <label className={inputFrame({ size, invalid, className, disabled: rest.disabled })} style={style}>
      {icon ? (
        <span className="rd-input-affix">{renderIcon(icon, 15, { color: "var(--rd-text-meta)" })}</span>
      ) : null}
      <Affix>{prefix}</Affix>
      <input ref={ref} aria-invalid={invalid || undefined} {...rest} />
      <Affix>{suffix}</Affix>
    </label>
  );
});

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { prefix, suffix, size, invalid, className, style, rows = 3, ...rest },
  ref,
) {
  return (
    <label className={inputFrame({ size, invalid, className, disabled: rest.disabled, area: true })} style={style}>
      <Affix>{prefix}</Affix>
      <textarea ref={ref} rows={rows} aria-invalid={invalid || undefined} {...rest} />
      <Affix>{suffix}</Affix>
    </label>
  );
});

export type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, "size"> & {
  size?: "sm" | "md" | "lg";
  invalid?: boolean;
  /** Options as data; or pass `<option>` children. */
  options?: Array<{ value: string; label: string; disabled?: boolean }>;
  /** A first, empty option ("Select a plan"). */
  placeholder?: string;
};

/** A native select in the input frame: keyboard, RTL and mobile pickers for free. */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size, invalid, className, style, options, placeholder, children, ...rest },
  ref,
) {
  return (
    <label className={inputFrame({ size, invalid, className, disabled: rest.disabled })} style={style}>
      <select ref={ref} className="rd-select" aria-invalid={invalid || undefined} {...rest}>
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>
    </label>
  );
});

export function SearchInput({
  className,
  style,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { className?: string; style?: CSSProperties }) {
  return (
    <label className={cx("rd-search", className)} style={style}>
      <Icon icon={Search} size={15} />
      <input type="search" {...rest} />
    </label>
  );
}

export function Field({
  label,
  help,
  error,
  layout = "stack",
  htmlFor,
  className,
  children,
}: {
  label?: ReactNode;
  help?: ReactNode;
  /** Replaces `help`; red 13px under the control. */
  error?: ReactNode;
  /** stack: label above · row: 140px label beside (edit forms). */
  layout?: "stack" | "row";
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx("rd-field", layout === "row" && "rd-field-row", className)}>
      {label != null ? (
        htmlFor ? (
          <label className="rd-field-label" htmlFor={htmlFor}>
            {label}
          </label>
        ) : (
          <span className="rd-field-label">{label}</span>
        )
      ) : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0, flex: layout === "row" ? 1 : undefined }}>
        {children}
        {error ? (
          <span className="rd-field-error" role="alert">
            {error}
          </span>
        ) : help ? (
          <span className="rd-field-help">{help}</span>
        ) : null}
      </div>
    </div>
  );
}

export function Toggle({
  checked,
  defaultChecked,
  onChange,
  size = "md",
  disabled,
  label,
  title,
  className,
}: {
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (next: boolean) => void;
  /** sm = 30×18, for table rows. */
  size?: "md" | "sm";
  disabled?: boolean;
  /** Accessible name. */
  label?: string;
  title?: string;
  className?: string;
}) {
  const [on, set] = useControlled(checked, Boolean(defaultChecked));
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={title}
      disabled={disabled}
      className={cx("rd-toggle", size === "sm" && "rd-toggle-sm", className)}
      onClick={(event) => {
        if (disabled) return;
        event.stopPropagation();
        set(!on);
        onChange?.(!on);
      }}
    >
      <span className="rd-toggle-knob" />
    </button>
  );
}

export function Checkbox({
  label,
  description,
  checked,
  defaultChecked,
  onChange,
  disabled,
  className,
}: {
  label?: ReactNode;
  description?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  const [on, set] = useControlled(checked, Boolean(defaultChecked));
  return (
    <label className={cx("rd-check", !description && "rd-check-plain", className)}>
      <input
        type="checkbox"
        className="rd-check-input"
        checked={on}
        disabled={disabled}
        onChange={(event) => {
          set(event.target.checked);
          onChange?.(event.target.checked);
        }}
      />
      <span className="rd-check-text">
        {label != null ? <span className="rd-check-label">{label}</span> : null}
        {description ? <span className="rd-check-desc">{description}</span> : null}
      </span>
    </label>
  );
}

export type SegmentOption<V extends string = string> = {
  value: V;
  label: ReactNode;
  icon?: IconRef;
  count?: number;
};

export function SegmentedControl<V extends string = string>({
  options,
  value,
  defaultValue,
  onChange,
  label,
  className,
}: {
  options: Array<SegmentOption<V>>;
  value?: V;
  defaultValue?: V;
  onChange?: (value: V) => void;
  /** Accessible name of the group. */
  label?: string;
  className?: string;
}) {
  const [current, set] = useControlled<V | undefined>(value, defaultValue ?? options[0]?.value);
  return (
    <div className={cx("rd-seg", className)} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="rd-seg-opt"
          aria-pressed={option.value === current}
          onClick={() => {
            set(option.value);
            onChange?.(option.value);
          }}
        >
          {option.icon ? renderIcon(option.icon, 15) : null}
          <span>{option.label}</span>
          {option.count != null ? <span className="rd-seg-count">{option.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function FilterChip({
  active,
  count,
  badge,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean;
  count?: number;
  /** A Doodle id shown at 22px before the label. */
  badge?: string;
}) {
  return (
    <button
      type="button"
      {...rest}
      aria-pressed={Boolean(active)}
      className={cx("rd-chip", badge && !active && "rd-chip-badge", className)}
    >
      {badge ? <Badge id={badge} size={22} shadow={false} /> : null}
      <span>{children}</span>
      {count != null ? <span className="rd-chip-count">{count}</span> : null}
    </button>
  );
}

export type Tone = "grey" | "green" | "blue" | "amber" | "orange" | "red" | "purple" | "pink" | "dark";
export type DotTone = "grey" | "green" | "blue" | "amber" | "red" | "purple";

/** A category or soft label: tint + its own dark ink. */
export function Tag({ tone = "grey", title, className, children }: { tone?: Tone; title?: string; className?: string; children: ReactNode }) {
  return (
    <span className={cx("rd-tag", `rd-tag-${tone}`, className)} title={title}>
      {children}
    </span>
  );
}

/** Status in tables and lists: a 7px dot and the word. Never the dot alone. */
export function StatusDot({ tone = "grey", className, children }: { tone?: DotTone; className?: string; children?: ReactNode }) {
  return (
    <span className={cx("rd-status", className)}>
      <span className={cx("rd-dot", `rd-dot-${tone}`)} />
      {children != null ? <span>{children}</span> : null}
    </span>
  );
}

export type TabItem<V extends string = string> = { value: V; label: ReactNode; count?: number };

export function Tabs<V extends string = string>({
  tabs,
  value,
  defaultValue,
  onChange,
  label,
  className,
}: {
  tabs: Array<TabItem<V>>;
  value?: V;
  defaultValue?: V;
  onChange?: (value: V) => void;
  label?: string;
  className?: string;
}) {
  const [current, set] = useControlled<V | undefined>(value, defaultValue ?? tabs[0]?.value);
  return (
    <div className={cx("rd-tabs", className)} role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={tab.value === current}
          className="rd-tab"
          onClick={() => {
            set(tab.value);
            onChange?.(tab.value);
          }}
        >
          <span>{tab.label}</span>
          {tab.count != null ? <span className="rd-tab-count">{tab.count}</span> : null}
        </button>
      ))}
    </div>
  );
}
